import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Save, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

// Tooltip wrapper component for consistent styling
const FieldTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button type="button" className="ml-2 text-amber-400 hover:text-amber-300 transition-colors inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400/20 hover:bg-amber-400/30 border border-amber-400/50">
          <Info size={12} strokeWidth={2.5} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-900 border-amber-500/50 text-slate-100 text-sm p-3 shadow-lg shadow-amber-500/10">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const Step2Executors = ({ data, onNext, onPrevious, onSaveAndExit, saving }) => {
  const [formData, setFormData] = useState({
    primary_executor: data.primary_executor || { name: '', relationship: '' },
    alternate_executor: data.alternate_executor || { name: '', relationship: '' },
    witness1: data.witness1 || { name: '', address: '' },
    witness2: data.witness2 || { name: '', address: '' }
  });

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const selectClass = "bg-slate-800 border-slate-700 text-white w-full p-2 rounded-md focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20";
  const labelClass = "text-slate-300 font-medium";

  const relationshipOptions = [
    'Spouse',
    'Son',
    'Daughter',
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grandson',
    'Granddaughter',
    'Nephew',
    'Niece',
    'Uncle',
    'Aunt',
    'Cousin',
    'Partner',
    'Friend',
    'Professional Advisor',
    'Other'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step2-form">
      {/* Primary Executor */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2 flex items-center">
          Primary Executor
          <FieldTooltip content="The Executor carries out your Will; they don't automatically receive assets. Ideally choose someone you can trust. It's advisable to avoid naming beneficiaries as executors." />
        </h3>
        <p className="text-sm text-slate-400">Person who will execute your will</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>Full Name *</Label>
            <Input
              type="text"
              value={formData.primary_executor.name}
              onChange={(e) => handleChange('primary_executor', 'name', e.target.value)}
              required
              className={inputClass}
              data-testid="primary-executor-name"
            />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Relationship *</Label>
            <select
              value={formData.primary_executor.relationship}
              onChange={(e) => handleChange('primary_executor', 'relationship', e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select</option>
              {relationshipOptions.map(rel => (
                <option key={rel} value={rel}>{rel}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Alternate Executor */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Alternate Executor (Optional)</h3>
        <p className="text-sm text-slate-400">Backup executor if primary is unable to serve</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>Full Name</Label>
            <Input
              type="text"
              value={formData.alternate_executor.name}
              onChange={(e) => handleChange('alternate_executor', 'name', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Relationship</Label>
            <select
              value={formData.alternate_executor.relationship}
              onChange={(e) => handleChange('alternate_executor', 'relationship', e.target.value)}
              className={selectClass}
            >
              <option value="">Select</option>
              {relationshipOptions.map(rel => (
                <option key={rel} value={rel}>{rel}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Witnesses */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Witnesses</h3>
        <p className="text-sm text-slate-400">Two witnesses are required for a valid Will</p>
        
        {/* Witness 1 */}
        <div className="bg-slate-800/50 p-4 rounded-lg space-y-4 border border-slate-700">
          <h4 className="text-white font-medium">Witness 1</h4>
          <div className="space-y-2">
            <Label className={labelClass}>Full Name *</Label>
            <Input
              type="text"
              value={formData.witness1.name}
              onChange={(e) => handleChange('witness1', 'name', e.target.value)}
              required
              className={inputClass}
              data-testid="witness1-name"
            />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Address *</Label>
            <textarea
              value={formData.witness1.address}
              onChange={(e) => handleChange('witness1', 'address', e.target.value)}
              rows="2"
              required
              className={`${inputClass} w-full rounded-md p-2`}
            ></textarea>
          </div>
        </div>

        {/* Witness 2 */}
        <div className="bg-slate-800/50 p-4 rounded-lg space-y-4 border border-slate-700">
          <h4 className="text-white font-medium">Witness 2</h4>
          <div className="space-y-2">
            <Label className={labelClass}>Full Name *</Label>
            <Input
              type="text"
              value={formData.witness2.name}
              onChange={(e) => handleChange('witness2', 'name', e.target.value)}
              required
              className={inputClass}
              data-testid="witness2-name"
            />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Address *</Label>
            <textarea
              value={formData.witness2.address}
              onChange={(e) => handleChange('witness2', 'address', e.target.value)}
              rows="2"
              required
              className={`${inputClass} w-full rounded-md p-2`}
            ></textarea>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <div className="flex gap-2">
          <Button type="button" onClick={onPrevious} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <ChevronLeft size={16} className="mr-2" /> Previous
          </Button>
          <Button type="button" onClick={() => onSaveAndExit(formData)} disabled={saving} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10">
            <Save size={16} className="mr-2" /> Save & Exit
          </Button>
        </div>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Next: Asset Categories <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step2Executors;
